const AUTH0_DOMAIN = import.meta.env.VITE_AUTH0_DOMAIN;
const AUTH0_CLIENT_ID = import.meta.env.VITE_AUTH0_CLIENT_ID;
const AUTH0_AUDIENCE = import.meta.env.VITE_AUTH0_AUDIENCE;
const AUTH0_CONNECTION =
  import.meta.env.VITE_AUTH0_CONNECTION || 'Username-Password-Authentication';

const STORAGE_KEYS = {
  accessToken: 'sindi_access_token',
  refreshToken: 'sindi_refresh_token',
  expiresAt: 'sindi_expires_at',
  organizationId: 'sindi_organization_id',
} as const;

export interface StoredTokens {
  accessToken: string | null;
  refreshToken: string | null;
  expiresAt: number;
}

export function loadStoredTokens(): StoredTokens {
  return {
    accessToken: localStorage.getItem(STORAGE_KEYS.accessToken),
    refreshToken: localStorage.getItem(STORAGE_KEYS.refreshToken),
    expiresAt: Number(localStorage.getItem(STORAGE_KEYS.expiresAt) || '0'),
  };
}

function storeTokens(
  accessToken: string,
  refreshToken: string,
  expiresIn: number
): StoredTokens {
  const expiresAt = Date.now() + expiresIn * 1000;
  localStorage.setItem(STORAGE_KEYS.accessToken, accessToken);
  localStorage.setItem(STORAGE_KEYS.refreshToken, refreshToken);
  localStorage.setItem(STORAGE_KEYS.expiresAt, String(expiresAt));
  return { accessToken, refreshToken, expiresAt };
}

export function clearStoredTokens(): void {
  localStorage.removeItem(STORAGE_KEYS.accessToken);
  localStorage.removeItem(STORAGE_KEYS.refreshToken);
  localStorage.removeItem(STORAGE_KEYS.expiresAt);
  localStorage.removeItem(STORAGE_KEYS.organizationId);
}

export function getOrganizationId(): string | null {
  return localStorage.getItem(STORAGE_KEYS.organizationId);
}

export function setOrganizationId(id: string): void {
  localStorage.setItem(STORAGE_KEYS.organizationId, id);
}

export function clearOrganizationId(): void {
  localStorage.removeItem(STORAGE_KEYS.organizationId);
}

export async function loginWithCredentials(
  email: string,
  password: string
): Promise<StoredTokens> {
  const response = await fetch(`https://${AUTH0_DOMAIN}/oauth/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      grant_type: 'password',
      client_id: AUTH0_CLIENT_ID,
      audience: AUTH0_AUDIENCE,
      connection: AUTH0_CONNECTION,
      username: email,
      password,
      scope: 'openid profile email offline_access',
    }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error_description || 'Invalid email or password');
  }

  const data = await response.json();
  return storeTokens(data.access_token, data.refresh_token, data.expires_in);
}

export async function refreshToken(
  currentRefreshToken: string
): Promise<StoredTokens> {
  const response = await fetch(`https://${AUTH0_DOMAIN}/oauth/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      grant_type: 'refresh_token',
      client_id: AUTH0_CLIENT_ID,
      refresh_token: currentRefreshToken,
    }),
  });

  if (!response.ok) {
    throw new Error('Token refresh failed');
  }

  const data = await response.json();
  return storeTokens(
    data.access_token,
    data.refresh_token || currentRefreshToken,
    data.expires_in
  );
}
